import {
  readBoPreferenceRaw,
} from "@/lib/user-preferences/store";

export type DashboardListView = "list" | "grid";

export type DashboardListScrollState = {
  listKey: string;
  entityId: string;
  scrollY: number;
  page: number;
  pageSize: number;
  view: DashboardListView;
  savedAt: number;
};

const STORAGE_KEY = "iz_bo_list_scroll";
const MAX_AGE_MS = 30 * 60 * 1000;

export const BO_LIST_ITEM_ID_ATTR = "data-bo-list-item-id";

/** Reads persisted list/grid tab preference (`{storageKey}-view`). */
export function readDashboardListViewPreference(
  viewStorageKey: string,
  defaultView: DashboardListView = "list",
): DashboardListView {
  try {
    const saved =
      readBoPreferenceRaw(viewStorageKey) ??
      localStorage.getItem(viewStorageKey);
    if (saved === "grid" || saved === "list") return saved;
    return defaultView;
  } catch {
    return defaultView;
  }
}

export function getDashboardListKey(pathname: string, search: string): string {
  const normalizedSearch =
    search.startsWith("?") || search === "" ? search : `?${search}`;
  return `${pathname}${normalizedSearch}`;
}

export function getDashboardListPathname(listKey: string): string {
  const qIndex = listKey.indexOf("?");
  return qIndex >= 0 ? listKey.slice(0, qIndex) : listKey;
}

function listKeysMatch(savedListKey: string, requestedListKey: string): boolean {
  if (savedListKey === requestedListKey) return true;
  return (
    getDashboardListPathname(savedListKey) ===
    getDashboardListPathname(requestedListKey)
  );
}

function readRawState(): DashboardListScrollState | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as DashboardListScrollState;
    if (
      typeof parsed.listKey !== "string" ||
      typeof parsed.entityId !== "string" ||
      typeof parsed.scrollY !== "number" ||
      typeof parsed.page !== "number" ||
      typeof parsed.pageSize !== "number" ||
      (parsed.view !== "list" && parsed.view !== "grid") ||
      typeof parsed.savedAt !== "number"
    ) {
      return null;
    }

    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/** Prefer the saved list URL (with query) when returning from a detail page on the same section. */
export function resolveDashboardListBackHref(pathname: string): string {
  const state = readRawState();
  return state && listKeysMatch(state.listKey, pathname) ? state.listKey : pathname;
}

export function saveDashboardListScroll(
  entityId: string,
  params: {
    listKey: string;
    page: number;
    pageSize: number;
    view: DashboardListView;
    scrollY?: number;
  },
): void {
  if (typeof window === "undefined") return;

  const state: DashboardListScrollState = {
    listKey: params.listKey,
    entityId,
    scrollY: params.scrollY ?? window.scrollY,
    page: params.page,
    pageSize: params.pageSize,
    view: params.view,
    savedAt: Date.now(),
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function peekDashboardListScroll(
  listKey: string,
): DashboardListScrollState | null {
  const state = readRawState();
  if (!state || !listKeysMatch(state.listKey, listKey)) return null;
  return state;
}

export function consumeDashboardListScroll(
  listKey: string,
): DashboardListScrollState | null {
  const state = peekDashboardListScroll(listKey);
  if (!state) return null;

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }

  return state;
}

export function scrollToDashboardListItem(
  entityId: string,
  scrollY: number,
): void {
  const selector = `[${BO_LIST_ITEM_ID_ATTR}="${CSS.escape(entityId)}"]`;
  const element = document.querySelector(selector);

  if (element) {
    element.scrollIntoView({ block: "center", behavior: "auto" });
    return;
  }

  window.scrollTo({ top: scrollY, behavior: "auto" });
}
